'use client';
import { Card, Spinner } from 'react-bootstrap';
import Button from 'components/base/Button';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faClock,
  faMapPin,
  faPoundSign,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';

interface Shift {
  booking_id: number;
  status: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  locum_role: string;
  pay_rate: number;
}

interface UpcomingShiftsCardProps {
  bookings: Shift[];
  loading: boolean;
  onCancel: (bookingId: number) => Promise<void>;
  cancelingId: number | null;
}

const UpcomingShiftsCard = ({
  bookings,
  loading,
  onCancel,
  cancelingId
}: UpcomingShiftsCardProps) => {
  const upcomingShifts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const in7Days = new Date(today);
    in7Days.setDate(in7Days.getDate() + 7);

    return bookings
      .filter(booking => {
        if (booking.status !== 'confirmed') return false;
        const bookingDate = new Date(booking.date);
        bookingDate.setHours(0, 0, 0, 0);
        return bookingDate >= today && bookingDate <= in7Days;
      })
      .sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.start_time}`);
        const dateB = new Date(`${b.date} ${b.start_time}`);
        return dateA.getTime() - dateB.getTime();
      });
  }, [bookings]);

  if (loading) {
    return (
      <Card className="border border-translucent shadow-sm h-100">
        <Card.Header className="bg-body-emphasis">
          <Card.Title className="mb-0 fs-8 text-body-emphasis">
            <FontAwesomeIcon icon={faCalendar} className="me-2" />
            Upcoming Shifts (Next 7 Days)
          </Card.Title>
        </Card.Header>
        <Card.Body className="text-center py-6">
          <Spinner animation="border" role="status" className="text-primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border border-translucent shadow-sm h-100">
      <Card.Header className="bg-body-emphasis">
        <Card.Title className="mb-0 fs-8 text-body-emphasis d-flex justify-content-between align-items-center">
          <span>
            <FontAwesomeIcon icon={faCalendar} className="me-2" />
            Upcoming Shifts (Next 7 Days)
          </span>
          <Badge variant="phoenix" bg="success" className="fs-10">
            {upcomingShifts.length}
          </Badge>
        </Card.Title>
      </Card.Header>
      <Card.Body className="p-3 p-lg-4">
        {upcomingShifts.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-body-tertiary mb-0">
              <FontAwesomeIcon icon={faCalendar} className="me-2 fs-6" />
              No upcoming shifts in the next 7 days
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingShifts.map(shift => (
              <div
                key={shift.booking_id}
                className="p-3 rounded-3 border border-translucent transition-all hover-card"
              >
                {/* 🔹 Top Row */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-0 fw-semibold text-body-emphasis">
                      {new Date(shift.date).toLocaleDateString('en-GB', {
                        weekday: 'short',
                        day: '2-digit',
                        month: 'short'
                      })}
                    </h6>
                    <small className="text-body-tertiary">
                      {shift.locum_role}
                    </small>
                  </div>

                  <Badge variant="phoenix" bg="success">
                    Confirmed
                  </Badge>
                </div>

                {/* 🔹 Info Row */}
                <div className="d-flex justify-content-between flex-wrap gap-2 mb-2">
                  <small className="text-body-tertiary">
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {shift.start_time.slice(0, 5)} -{' '}
                    {shift.end_time.slice(0, 5)}
                  </small>

                  <small className="text-body-tertiary text-truncate">
                    <FontAwesomeIcon icon={faMapPin} className="me-1" />
                    {shift.location}
                  </small>
                </div>

                {/* 🔹 Bottom Row */}
                <div className="d-flex justify-content-between align-items-center">
                  <div className="fw-semibold text-success">
                    <FontAwesomeIcon icon={faPoundSign} className="me-1" />
                    {shift.pay_rate}/hr
                  </div>

                  <Button
                    color="danger"
                    size="sm"
                    className="px-3"
                    disabled={cancelingId === shift.booking_id}
                    onClick={() => onCancel(shift.booking_id)}
                  >
                    {cancelingId === shift.booking_id ? (
                      <Spinner size="sm" className="me-1" />
                    ) : (
                      <FontAwesomeIcon icon={faTimes} className="me-1" />
                    )}
                    {cancelingId === shift.booking_id ? 'Cancelling' : 'Cancel'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default UpcomingShiftsCard;
